import axiosApiClient from '..';
import { handleError, throwErrorIfWrongStatus } from './utils';

const BASE_URL = '/d/influencer-requests';

class InfluencerRequestService {
  createInfluencerRequestPassword = async ({ password }) => {
    try {
      const body = {
        password,
      };
      const response = await axiosApiClient.put(`${BASE_URL}/update-password`, body);
      throwErrorIfWrongStatus(response, 200);
      const { userStatus } = response.data.data;
      return { userStatus };
    } catch (err) {
      throw handleError(err, "Error occurred while setting user's password.");
    }
  };

  updateBasicInfo = async body => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/update-basic-info`, body);
      throwErrorIfWrongStatus(response, 200);
      const { user } = response.data.data;
      return { user };
    } catch (err) {
      throw handleError(err, 'Error occurred while updating basic info.');
    }
  };

  updateTelegramInfo = async formData => {
    try {
      const headers = { 'Content-Type': 'multipart/form-data' };
      const response = await axiosApiClient.post(`${BASE_URL}/update-telegram-info`, formData, {
        headers,
      });
      throwErrorIfWrongStatus(response, 200);
      const { user } = response.data.data;
      return { user };
    } catch (err) {
      throw handleError(err, 'Error occurred while updating telegram info.');
    }
  };

  uploadSelfPhoto = async (formData, onUploadProgress) => {
    try {
      const headers = { 'Content-Type': 'multipart/form-data' };
      const response = await axiosApiClient.post(`${BASE_URL}/upload-photo/self`, formData, {
        headers,
        onUploadProgress,
      });
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading photo.');
    }
  };

  startUploadAssistedPhoto = async formData => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/upload-photo/assisted/start`, formData);
      throwErrorIfWrongStatus(response, 200);
      const { url, filename } = response.data.data;
      return { url, filename };
    } catch (err) {
      throw handleError(err, 'Error occurred while starting upload photo.');
    }
  };

  completeUploadAssistedPhoto = async body => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/upload-photo/assisted/complete-upload`, body);
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while completing photo upload.');
    }
  };

  uploadSelfVideo = async (formData, onUploadProgress) => {
    try {
      const headers = { 'Content-Type': 'multipart/form-data' };
      const response = await axiosApiClient.post(`${BASE_URL}/upload-video/self`, formData, {
        headers,
        onUploadProgress,
      });
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading video.');
    }
  };

  startUploadAssistedVideo = async body => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/upload-video/assisted/start`, body);
      throwErrorIfWrongStatus(response, 200);
      const { uploadId, filename } = response.data.data;
      return { uploadId, filename };
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading video.');
    }
  };

  getUploadAssistedVideoUrl = async body => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/upload-video/assisted/get-upload-url`, body);
      throwErrorIfWrongStatus(response, 200);
      const { url } = response.data.data;
      return { url };
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading video.');
    }
  };

  completeUploadAssistedVideo = async body => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/upload-video/assisted/complete-upload`, body);
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading video.');
    }
  };

  uploadBundle = async (formData, onUploadProgress) => {
    try {
      const headers = { 'Content-Type': 'multipart/form-data' };
      const response = await axiosApiClient.post(`${BASE_URL}/upload-bundle`, formData, {
        headers,
        onUploadProgress,
      });
      throwErrorIfWrongStatus(response, 200);
      const { userStatus } = response.data.data;
      return { userStatus };
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading bundle.');
    }
  };

  saveGallery = async () => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/save-gallery`);
      throwErrorIfWrongStatus(response, 200);
      const { userStatus } = response.data.data;
      return { userStatus };
    } catch (err) {
      throw handleError(err, 'Error occurred while saving gallery.');
    }
  };

  saveQuestionnaireAnswers = async body => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/questionnaire`, body);
      throwErrorIfWrongStatus(response, 200);
      const { userStatus } = response.data.data;
      return { userStatus };
    } catch (err) {
      throw handleError(err, 'Error occurred while saving questionnaire.');
    }
  };

  saveWritingStyleAnswers = async body => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/writing-styles`, body);
      throwErrorIfWrongStatus(response, 200);
      const { userStatus } = response.data.data;
      return { userStatus };
    } catch (err) {
      throw handleError(err, 'Error occurred while saving writing style.');
    }
  };

  uploadAudio = async formData => {
    try {
      const headers = { 'Content-Type': 'multipart/form-data' };
      const response = await axiosApiClient.post(`${BASE_URL}/upload-audio`, formData, {
        headers,
      });
      throwErrorIfWrongStatus(response, 200);
      const { voiceId } = response.data.data;
      return { voiceId };
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading audio.');
    }
  };

  uploadAudioElevenLabs = async formData => {
    try {
      const headers = { 'Content-Type': 'multipart/form-data' };
      const response = await axiosApiClient.post(`${BASE_URL}/upload-audio-eleven-labs`, formData, {
        headers,
      });
      throwErrorIfWrongStatus(response, 200);
      const { voiceId } = response.data.data;
      return { voiceId };
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading audio.');
    }
  };

  uploadHighQualityAudio = async formData => {
    try {
      const headers = { 'Content-Type': 'multipart/form-data' };
      const response = await axiosApiClient.post(`${BASE_URL}/upload-high-quality-audio`, formData, {
        headers,
      });
      throwErrorIfWrongStatus(response, 200);
      const { userStatus } = response.data.data;
      return { userStatus };
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading audio.');
    }
  };

  testVoice = async (voiceId, message, voiceSpeed) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/test-voice`, { voiceId, message, voiceSpeed });
      throwErrorIfWrongStatus(response, 200);
      return response.data.data;
    } catch (err) {
      throw handleError(err, 'Error occurred while testing voice.');
    }
  };

  testVoiceElevenLabs = async (message, stability) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/test-voice-eleven-labs`, {
        stability,
        message,
      });
      throwErrorIfWrongStatus(response, 200);
      return response.data.data;
    } catch (err) {
      throw handleError(err, 'Error occurred while testing voice.');
    }
  };

  saveVoice = async (voiceId, voiceSpeed) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/save-voice`, { voiceId, voiceSpeed });
      throwErrorIfWrongStatus(response, 200);
      const { userStatus } = response.data.data;
      return { userStatus };
    } catch (err) {
      throw handleError(err, 'Error occurred while saving voice.');
    }
  };

  saveVoiceElevenLabs = async stability => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/save-voice-eleven-labs`, {
        ...(stability && { stability }),
      });
      throwErrorIfWrongStatus(response, 200);
      const { userStatus } = response.data.data;
      return { userStatus };
    } catch (err) {
      throw handleError(err, 'Error occurred while saving voice.');
    }
  };

  getOrCreateIdVerificationToken = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/verification`);
      throwErrorIfWrongStatus(response, 200);
      const { sessionUrl, userStatus } = response.data.data;
      return { sessionUrl, userStatus };
    } catch (err) {
      throw handleError(err, 'Error occurred while getting identify verification token.');
    }
  };

  getQuestionnaire = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/questionnaire`);
      throwErrorIfWrongStatus(response, 200);
      const { questionnaire } = response.data.data;
      return { questionnaire };
    } catch (err) {
      throw handleError(err, 'Error occurred while getting questionnaire.');
    }
  };

  getAlbumMediaTags = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/album-media-tags`);
      throwErrorIfWrongStatus(response, 200);
      const { albumMediaTags } = response.data.data;
      return { albumMediaTags };
    } catch (err) {
      throw handleError(err, 'Error occurred while getting album media tags.');
    }
  };

  updateOnboardingType = async ({ onboardingType }) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/onboarding-type`, { onboardingType });
      throwErrorIfWrongStatus(response, 200);
      return { onboardingType };
    } catch (err) {
      throw handleError(err, 'Error occurred while updating onboarding type.');
    }
  };

  updateSubscriptionPrice = async ({ subscriptionPrice }) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/subscription-price`, { subscriptionPrice });
      throwErrorIfWrongStatus(response, 200);
      const { userStatus } = response.data.data;
      return { userStatus };
    } catch (err) {
      throw handleError(err, 'Error occurred while updating onboarding type.');
    }
  };

  fetchInfluencerRequestAlbumMedia = async (tag, kind) => {
    try {
      const params = new URLSearchParams();
      if (tag) params.append('tag', tag);
      if (kind) params.append('kind', kind);
      const response = await axiosApiClient.get(`${BASE_URL}/album-media?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
      const { data } = response.data.data;
      return { data };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching album media.');
    }
  };

  deleteInfluencerRequestAlbumMedia = async albumMediaId => {
    try {
      const response = await axiosApiClient.delete(`${BASE_URL}/${albumMediaId}`);
      throwErrorIfWrongStatus(response, 200);
      return { albumMediaId };
    } catch (err) {
      throw handleError(err, 'Error occurred while deleting album media');
    }
  };

  setMediaPricing = async body => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/media-pricing`, body);
      throwErrorIfWrongStatus(response, 200);
      const { userStatus } = response.data.data;
      return { userStatus };
    } catch (err) {
      throw handleError(err, 'Error occurred while deleting album media');
    }
  };
}

export default new InfluencerRequestService();
